import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  faBars,
  faChevronRight,
  faRightFromBracket,
  faRightToBracket,
  faBagShopping,
  faCartShopping,
  faBoxOpen,
  faReceipt,
  faHeart,
  faStore,
  faCircleQuestion,
  faCircleInfo,
  faHandHoldingHeart,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logout } from "../../redux/actions/authActions";

const menuItems = [
  {
    title: "Browse Products",
    description: "Browse products from local women entrepreneurs.",
    icon: faBagShopping,
    route: "/herkart/products",
  },
  {
    title: "Browse Private Stores",
    description: "Browse products from local women entrepreneurs.",
    icon: faBagShopping,
    route: "/herkart/stores-around-me",
  },
  {
    title: "Services Around Me",
    description: "Browse Services in Local Area.",
    icon: faBagShopping,
    route: "/herkart/services",
  },
  {
    title: "My Cart",
    description: "Review your selected items.",
    icon: faCartShopping,
    route: "/herkart/cart",
  },
  {
    title: "My Orders",
    description: "Track current and previous orders.",
    icon: faBoxOpen,
    route: "/herkart/orders/mine",
  },
  // {
  //   title: "Bills",
  //   description: "Download invoices.",
  //   icon: faReceipt,
  //   route: "/herkart/bills",
  // },
  {
    title: "Saved Items",
    description: "Products saved for later.",
    icon: faHeart,
    route: "/herkart/saved",
  },
  // {
  //   title: "Stores",
  //   description: "Browse Participating Stores.",
  //   icon: faStore,
  //   route: "/herkart/stores",
  // },
  // {
  //   title: "Help & Support",
  //   description: "Need assistance?",
  //   icon: faCircleQuestion,
  //   route: "/contact-us",
  // },
  {
    title: "How COD Works",
    description: "Understand pickup and delivery.",
    icon: faCircleInfo,
    route: "/herkart/how-cod-works",
  },
];

const HerKartHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer);

  const isLoggedIn = Boolean(authState?.token);

  const userName = useMemo(() => {
    return (
      authState?.user?.name ||
      authState?.user?.fullName ||
      authState?.user?.firstName ||
      "User"
    );
  }, [authState]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-900 to-black text-white">
      <header className="sticky top-0 z-50 border-b border-fuchsia-500/20 backdrop-blur-xl bg-black/70">
        <div className="bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-700">
          <div className="mx-auto max-w-7xl px-4 py-2">
            <div className="flex flex-row items-start justify-between">
              <div
                onClick={() => navigate("/herkart")}
                className="flex flex-row cursor-pointer items-start gap-3"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                  <span className="text-2xl">🛍️</span>
                </div>

                <div className="flex flex-col items-start">
                  <h1 className="text-xl font-bold tracking-wide items-start">
                    HerKart
                  </h1>

                  <p className="text-xs text-pink-100">
                    Shop Local • Empower Women Founders
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {isLoggedIn ? (
                  <>
                    <span className="hidden md:block text-xs font-medium">
                      Hi, {userName}
                    </span>

                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 rounded-xl bg-white/15 w-fit px-2 py-1 text-sm transition hover:bg-white/25"
                    >
                      <FontAwesomeIcon icon={faRightFromBracket} />
                      <span className="hidden sm:block">Logout</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    className="rounded-xl bg-white w-fit px-2 py-1 font-semibold text-pink-700 transition hover:scale-105"
                  >
                    <FontAwesomeIcon icon={faRightToBracket} className="mr-2" />
                    Login
                  </button>
                )}

                <button className="rounded-xl w-fit px-2 py-1 bg-white/15 p-3 transition hover:bg-white/25 md:hidden">
                  <FontAwesomeIcon icon={faBars} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-2">
        <section className="overflow-hidden rounded-3xl border border-fuchsia-500/20 bg-gradient-to-br from-pink-700/20 via-fuchsia-700/10 to-purple-700/20 px-6 py-2 shadow-2xl">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-pink-500/20 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-pink-300">
              Young Generation Marketplace
            </span>
          </div>

          <div className="mt-2 flex items-center gap-4 rounded-3xl border border-pink-500/20 bg-gradient-to-r from-pink-900/30 via-fuchsia-900/20 to-purple-900/30 px-5 py-1">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-pink-500/20 text-2xl">
              💖
            </div>

            <div>
              <div className="mb-1 flex items-center gap-2 text-pink-300">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
                <span className="font-semibold">Empowering Local Women</span>
              </div>

              <p className="text-sm text-gray-300">
                Every purchase supports a local woman entrepreneur and
                strengthens your neighbourhood economy.
              </p>
            </div>
          </div>
          <div className="mt-2 rounded-2xl border border-pink-500 bg-black/30 px-4 py-1 text-sm text-pink-100">
            Order online. Pay only during pickup or delivery.
          </div>
        </section>

        <section className="mt-2">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {menuItems.map((item) => (
              <FeatureCard
                key={item.title}
                item={item}
                onClick={() => navigate(item.route)}
              />
            ))}
          </div>
        </section>
        <Footer />
      </main>
    </div>
  );
};

const FeatureCard = ({ item, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full flex-col rounded-3xl border border-fuchsia-500/20 bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-950 p-5 text-left shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-pink-500/40 hover:shadow-pink-500/20"
    >
      <div className="flex items-center justify-between">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600/20 via-fuchsia-600/20 to-purple-600/20 text-pink-300 transition group-hover:scale-110">
          <FontAwesomeIcon icon={item.icon} size="lg" />
        </div>

        <FontAwesomeIcon
          icon={faChevronRight}
          className="text-gray-500 transition group-hover:translate-x-1 group-hover:text-pink-300"
        />
      </div>

      <h3 className="mt-5 text-base font-bold text-white">{item.title}</h3>

      <p className="mt-2 text-sm leading-6 text-gray-400">{item.description}</p>

      <div className="mt-auto pt-5">
        <span className="text-sm font-medium text-pink-300 transition group-hover:text-pink-200">
          Open
        </span>
      </div>
    </button>
  );
};

const Footer = () => {
  return (
    <footer className="mt-12 border-t border-fuchsia-500/20 py-8">
      <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
        <div className="text-center md:text-left">
          <h4 className="text-lg font-bold text-white">HerKart</h4>

          <p className="mt-2 text-sm text-gray-400">
            Shop Local • Empower Women Founders
          </p>

          <p className="mt-3 text-xs text-gray-500">
            © {new Date().getFullYear()} Think5Whys Global DeepTech Solutions.
            All rights reserved.
          </p>
        </div>

        <nav className="flex flex-wrap items-center justify-center gap-5 text-sm text-gray-400">
          <Link to="/about" className="transition hover:text-pink-300">
            About
          </Link>

          <Link to="/privacy-policy" className="transition hover:text-pink-300">
            Privacy
          </Link>

          <Link
            to="/terms-and-conditions"
            className="transition hover:text-pink-300"
          >
            Terms
          </Link>

          <Link to="/contact-us" className="transition hover:text-pink-300">
            Contact
          </Link>
        </nav>
      </div>
    </footer>
  );
};

export default HerKartHome;
